import { action, observable } from 'mobx';
import { showInfoToast } from 'core/components';
import { api, Socket } from 'core/util';
import JourneyCollection from 'app/views/core/journeys/JourneyCollection';
import ChatSessionCollection from 'app/views/core/kaizen/models/ChatSessionCollection';
import { uuidv7 } from 'uuidv7';

const supportsNotifications =
  typeof window !== 'undefined' && 'Notification' in window && typeof Notification.requestPermission === 'function';

class JourneysStore {
  // Journeys v1
  collection = new JourneyCollection();

  // Journeys v2 chat sessions
  chatSessionCollection = new ChatSessionCollection();

  // This is used to set the context for the copilot chat
  // Consumers that want to set the context for the copilot chat can call
  // $journeys.setChatContext(context)
  // where context is an object with the following properties:
  // - type: string (e.g. 'device', 'interface', 'cloud vpc')
  // - name: string (e.g. 'agg203.kentik.com')
  // - placeholder: string (e.g. 'Ask about this device')
  // - context: string (e.g. 'Full device details: {...}',
  @observable
  activeChatContext = new Set();

  socket = null;

  @observable
  isCopilotOpen = false;

  @action
  openCopilot() {
    this.isCopilotOpen = true;
  }

  @action
  closeCopilot() {
    this.isCopilotOpen = false;
    this.chatSessionCollection.clearSelection();
    this.activeChatContext.clear();
  }

  @action
  minimizeCopilot() {
    this.isCopilotOpen = false;
  }

  @action
  toggleCopilot() {
    this.isCopilotOpen = !this.isCopilotOpen;
  }

  @action
  addChatContext = (context) => {
    console.info('Kaizen: Adding chat context', context);

    // Check if context with this name already exists
    const existingContext = Array.from(this.activeChatContext).find((ctx) => ctx.name === context.name);

    if (existingContext) {
      console.info('Context with name already exists, replacing:', context.name);
      this.activeChatContext.delete(existingContext);
    }

    this.activeChatContext.add(context);
  };

  @action
  clearAllChatContext = () => {
    console.info('Kaizen: Clearing all chat context');
    this.activeChatContext.clear();
  };

  @action
  removeChatContext = (context) => {
    console.info('Kaizen: Removing chat context', context);

    const contextToRemove = Array.from(this.activeChatContext).find((ctx) => ctx.name === context.name);
    if (contextToRemove) {
      this.activeChatContext.delete(contextToRemove);
    }
  };

  serializeChatContext() {
    if (this.activeChatContext.size === 0) {
      return '';
    }

    return Array.from(this.activeChatContext)
      .map((context) => `${context.type}: ${context.name} - ${context.context}`)
      .join('\n');
  }

  // For testing purposes - can be called from browser console
  // e.g. window.$store.$journeys.testCopilot()
  testCopilot() {
    this.toggleCopilot();
  }

  get baseUrl() {
    return '/v4/core/ai-advisor';
  }

  @action
  createJourney = () => {
    // initializing with id here so it can be hashed when adding to collection
    const newJourney = this.chatSessionCollection.forge({ id: uuidv7() });
    this.chatSessionCollection.add(newJourney);
    return newJourney;
  };

  @action
  initialize() {
    if (supportsNotifications) {
      Notification.requestPermission();

      this.socket = new Socket({
        outType: 'journeysNotifications',
        inType: 'journeysNotifications',
        frequency: 60,
        delaySend: true,
        listenToBroadcast: true,
        onSuccess: this.onJourneyNotification,
        onError(err) {
          console.warn('Received Journeys Notifications Socket Error', err);
        },
        onReconnect: () => {
          this.socket.send();
        }
      });

      this.socket.send();
    }
  }

  onJourneyNotification = (data) => {
    console.info('Received Journeys Notification', data);

    let notification;

    const [journey] = this.collection.add({ id: data.id });

    const onClick = () => {
      const { id } = data;

      this.history.push(`/v4/core/journeys/${id}`);
      if (notification) {
        notification.close();
      }
    };

    journey.fetch().then(() => {
      if (Notification.permission === 'granted') {
        console.info('Sending browser notification');
        notification = new Notification(data.title, {
          icon: 'https://images.ctfassets.net/6yom6slo28h2/33eo83sVM9DGq3WCFA5Uh5/70df4c8ad0906763e6a4d174cef5652c/kentik-ai-badge.png',
          body: data.description
        });

        this.store.$basicNotifications.addNotification({
          id: data.id,
          path: `/v4/core/journeys/${data.id}`,
          message: data.description,
          title: data.title,
          timestamp: new Date()
        });

        notification.onclick = onClick;
      } else {
        console.info('Showing info toast');
        showInfoToast(data.description, { title: data.title, action: { onClick, text: 'Launch Journey' } });
      }
    });
  };

  startInvestigation(data) {
    return api.post('/api/ui/journeys/investigation/start', { data });
  }

  continueInvestigation(data) {
    return api.post('/api/ui/journeys/investigation/continue', { data });
  }
}

export default new JourneysStore();
